import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

import {
  chatPagination as chatPaginationSelector,
  formatMessages,
  getActiveOptionComp,
  getAppSSID,
  getIsMaximized,
  selectFaqLoader,
  selectIsDisabled,
  selectIsFaq,
  selectMessages,
  selectPropertyId,
  setFaqLoader,
  setPagination,
  stopScroll,
  updateScroll,
  selectReplies,
  setShowAllOptions,
} from "slices";
import { loadFaqChat, sortedByDate, formatTimestamp } from "utils";
import { AMAOptions, CustomTooltip } from "components";
import { getShowFaq } from "slices/property-slice/index";

import Message from "./Message";

const MessagesList = (props) => {
  // const timeoutRef = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [messageDay, setMessageDay] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const messages = useSelector(selectMessages);
  const chatPagination = useSelector(chatPaginationSelector);
  const isScroll = useSelector(stopScroll);
  const propertyId = useSelector(selectPropertyId);
  const isFaq = useSelector(selectIsFaq);
  const faqLoader = useSelector(selectFaqLoader);
  const isMaximized = useSelector(getIsMaximized);
  const activeOptionComp = useSelector(getActiveOptionComp);
  const optionDisabled = useSelector(selectIsDisabled);
  const SSID = useSelector(getAppSSID);
  const selectedReply = useSelector(selectReplies);
  const { i18n } = useTranslation();
  const observer1 = useRef(null);
  const firstMsgRef = useRef(null);
  const stopObserving = useRef(false);
  const listRef = useRef(null);
  const childRef = useRef(null);
  const observer = useRef(null);
  const faqIndexRef = useRef(0);
  const lastMessageRef = useRef(null);

  const shouldHideAMA = ["Form4ScheduleTour", "Form4ContactUs"].includes(selectedReply.optionsType);
  const showAMA = useSelector(getShowFaq);
  const [lang, setLang] = useState("Spanish");
  const [faqError, setFaqError] = useState(false);

  const height = "calc(100%)";

  const dispatch = useDispatch();

  const loadMoreRef = useCallback(
    (node) => {
      if (chatPagination.page === 1) {
        faqIndexRef.current = 0;
      }

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !faqLoader && isFaq && !faqError && !optionDisabled && chatPagination.loadMore) {
          handlePagination();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [chatPagination, messages, optionDisabled, isFaq, faqLoader]
  );

  useEffect(() => {
    if (!isFaq) {
      setFaqError(false);
    }
  }, [isFaq]);

  useEffect(() => {
    const isSelectSpanish = i18n.language === "sp";

    const val = isSelectSpanish ? "Spanish" : "English";

    setLang(val);
  }, [i18n.language]);

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  useEffect(() => {
    if (loadingMore) {
      return;
    }

    if (listRef.current && !isScroll) {
      if (faqLoader) {
        setTimeout(() => {
          updateFaqLoader(false);
        }, [100]);

        const childTopHeight = childRef.current?.offsetTop || 0;

        listRef.current.scrollTo({
          top: childTopHeight - 20 || listRef.current.scrollHeight,
        });
      } else {
        if (lastMessageRef.current) {
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }

        // if (timeoutRef.current) {
        //   clearTimeout(timeoutRef.current);
        // }

        // const delay = lastMsg.message.includes("bounce-dot") || lastMsg.speaker === "user" ? 200 : 10;

        // timeoutRef.current = setTimeout(() => {
        //   scrollToBottom();
        // }, delay);
      }
    }
  }, [messages, height, isMaximized, activeOptionComp, groupMessages, lastMessageRef, chatPagination.loadedFromStorage]);

  useEffect(() => {
    if (isScroll) {
      dispatch(updateScroll(false));
    }
  }, [messages]);

  const updateFaqLoader = (val) => {
    dispatch(setFaqLoader(val));
  };

  const handlePagination = () => {
    const perPage = chatPagination.per_page;

    updateFaqLoader(true);

    loadFaqChat(SSID, { ...chatPagination, page: chatPagination.page }, propertyId)
      .then((newMsgs) => {
        const startIndex = messages.length - chatPagination.lastResLength || 0;

        faqIndexRef.current = startIndex + newMsgs.length;

        const updatedMessages = [...newMsgs.sort(sortedByDate), ...messages.slice(0, startIndex), ...messages.slice(startIndex)];

        if (newMsgs.length > 0) {
          dispatch(formatMessages(updatedMessages));
        } else {
          updateFaqLoader(false);
        }

        dispatch(
          setPagination({
            ...chatPagination,
            page: chatPagination.page + 1,
            loadMore: newMsgs.length === perPage,
            lastResLength: chatPagination.lastResLength + newMsgs.length,
          })
        );
      })
      .catch((err) => {
        updateFaqLoader(false);
        setFaqError(true);
        console.error(err);
      });
  };

  useEffect(() => {
    stopObserving.current = true;
    const updatedMessages = [...messages];
    let currentDay = null;
    for (let i = 0; i < updatedMessages.length; i++) {
      let message = updatedMessages[i];

      if (!message.createdAt) {
        message = { ...message, createdAt: new Date() };
      }

      let timestamp = moment(new Date(message.createdAt)).startOf("day");
      let today = moment().startOf("day");

      const day = timestamp.isSame(today, "day")
        ? "Today"
        : timestamp.isSame(today.subtract(1, "days"), "day")
        ? "Yesterday"
        : timestamp.format("MMM DD, YYYY");

      if (day !== currentDay) {
        updatedMessages.splice(i, 0, { day, speaker: "bot", message: "" });
        currentDay = day;
        i++;
      }
    }

    const messageArray = updatedMessages.length > 2 ? updatedMessages : updatedMessages.slice(1);
    const slicedMsgs = messageArray.slice(-(chatPagination.page * chatPagination.per_page));
    // const msgs = assignUniqueKeysToMessages(slicedMsgs);

    setGroupMessages(slicedMsgs);

    setTimeout(() => {
      stopObserving.current = false;
    }, 1000);
  }, [messages, chatPagination.page]);

  const loadMoreMessages = () => {
    if (stopObserving.current) {
      return;
    }

    setLoadingMore(true);
    let totalPages = Math.ceil(messages.length / chatPagination.per_page);

    if (!faqLoader && totalPages >= chatPagination.page) {
      if (totalPages > chatPagination.page) {
        scrollDown();
      }

      dispatch(
        setPagination({
          ...chatPagination,
          page: chatPagination.page + 1,
        })
      );
    }

    setTimeout(() => {
      setLoadingMore(false);
    }, 100);
  };

  const scrollDown = () => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: 1550 });

      setTimeout(() => {
        listRef.current.scrollTo({
          top: 1550,
        });
      }, 1);
    }
  };

  useEffect(() => {
    if (observer1.current) {
      observer1.current.disconnect();
    }

    const currentRef = firstMsgRef.current;

    observer1.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreMessages();
        }
      },
      { root: listRef.current }
    );

    if (currentRef) {
      observer1.current.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer1.current.unobserve(currentRef);
      }
    };
  }, [groupMessages, chatPagination.page, observer1.current]);

  useEffect(() => {
    let scrollTimer;

    const handleScroll = () => {
      clearTimeout(scrollTimer);

      const timestampElements = Array.from(document.querySelectorAll("[data-timestamp]"));

      const visibleElements = timestampElements.filter((element) => {
        const rect = element.getBoundingClientRect();

        return rect.top >= 0 && rect.bottom <= listRef?.current?.clientHeight;
      });

      visibleElements.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

      const topVisibleElement = visibleElements[1];

      if (topVisibleElement) {
        const timestamp = topVisibleElement.getAttribute("data-timestamp");
        setMessageDay(formatTimestamp(timestamp));
      } else {
        setMessageDay("");
      }

      scrollTimer = setTimeout(() => {
        setMessageDay("");
      }, 500); // Adjust the delay as needed
    };

    listRef.current?.addEventListener("scroll", handleScroll);

    return () => {
      listRef.current?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const showDate = (_g) => _g.filter((m) => m.day).length > 1;

  useEffect(() => {
    const listElement = listRef.current;

    const handleScroll = () => {
      if (listElement && lastMessageRef.current) {
        const lastMessageRect = lastMessageRef.current.getBoundingClientRect();
        const listRect = listElement.getBoundingClientRect();

        // Check if the top of the last message is above the bottom of the list container
        const isLastMessageAboveView = lastMessageRect.top > listRect.bottom;

        setShowScrollToBottom(isLastMessageAboveView);
      }
    };

    listElement.addEventListener("scroll", handleScroll);

    return () => {
      listElement.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const renderScrollToBottomIcon = () => {
    return (
      <CustomTooltip title={"Jump to latest"} placement="bottom">
        <div
          className={`hayley-fixed   ${
            isMaximized ? "hayley-bottom-32 hayley-right-11" : "hayley-bottom-24 hayley-right-4"
          }  hayley-flex hayley-h-10 hayley-w-10 hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-rounded-full hayley-bg-white`}
          style={{
            border: "1px solid #EEEEEE",
            boxShadow: "0px 6px 16px 0px #99999914",
            display: showScrollToBottom ? "flex" : "none",
          }}
          onClick={scrollToBottom}
        >
          <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 5L8 11L2 5" stroke="#333333" strokeWidth="1.5" strokeMiterlimit={10} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </CustomTooltip>
    );
  };

  return (
    <>
      {messageDay && showDate(groupMessages) && (
        <div
          className="hayley-fixed hayley-left-[50%] hayley-top-28  hayley-z-50 hayley-translate-x-[-50%] hayley-rounded-[20px] hayley-border-[1px]  hayley-bg-white hayley-px-[14px] hayley-py-[7px]"
          style={{ border: "1px solid #EEEEEE" }}
        >
          <span className="text-[12px] text-[#333333]">{messageDay}</span>
        </div>
      )}

      <div
        className={`hayley-box-border  hayley-w-full hayley-overflow-auto hayley-px-4 hayley-py-4 ${isMaximized ? "hayley-space-y-4" : "hayley-space-y-3"}`}
        style={{ height }}
        ref={listRef}
        onClick={() => dispatch(setShowAllOptions(false))}
      >
        {groupMessages?.map((message, index, arr) => {
          const lastValidIndex = [...arr].reverse().findIndex((msg) => msg.message);

          const adjustedLastValidIndex = lastValidIndex !== -1 ? arr.length - 1 - lastValidIndex : arr.length - 1;

          return (
            <div
              key={message?.id || message?.createdAt}
              ref={index === 0 ? firstMsgRef : index === adjustedLastValidIndex ? lastMessageRef : undefined}
              className={index == groupMessages.length - 1 && chatPagination.loadedFromStorage ? "!hayley-mb-[85%] md:!hayley-mb-[60%]" : "hayley-mb-1"}
            >
              {!!message?.day && showDate(groupMessages) ? (
                <div className="hayley-flex hayley-w-full hayley-items-center hayley-justify-center">
                  <div
                    className="hayley-rounded-[20px] hayley-border-[1px] hayley-bg-white  hayley-px-[14px] hayley-py-[7px]"
                    style={{ border: "1px solid #EEEEEE" }}
                  >
                    <span className="text-[12px] text-[#333333] ">{message?.day}</span>
                  </div>
                </div>
              ) : null}

              {message.showLangChangeAlert ? (
                <p className="hayley-m-0 hayley-text-center hayley-text-xs hayley-font-semibold hayley-leading-[18px] hayley-text-custom-4">
                  {message.message}
                </p>
              ) : null}

              {!message?.day && !message.showLangChangeAlert ? (
                <Message
                  mascotSettings={props.mascotSettings}
                  position={props.position}
                  data={message}
                  loadMoreRef={loadMoreRef}
                  childRef={childRef}
                  isScrollableFaq={faqIndexRef.current - 1 === index}
                  key={index}
                  index={index}
                  lang={lang}
                  isMobile={props.isMobile}
                  messagesContainerRef={listRef}
                  setMessageDay={setMessageDay}
                  messageDay={messageDay}
                  messages={groupMessages}
                />
              ) : null}
            </div>
          );
        })}
        {renderScrollToBottomIcon()}
      </div>
      {shouldHideAMA || !showAMA ? null : (
        <div className={`hayley-relative hayley-bottom-0 hayley-left-0 hayley-right-0 hayley-bg-white hayley-shadow-onesided`}>
          <AMAOptions />
        </div>
      )}
    </>
  );
};

export default MessagesList;
